// Minimal InnerTube transport (YouTube's internal API). Environment-neutral: works in Node and,
// thanks to the request shape below, directly from a browser on any origin (no proxy).
//
// CORS facts (verified Oct 2026): www.youtube.com/youtubei/v1/* answers *simple* cross-origin
// requests with `Access-Control-Allow-Origin: <origin>` (no credentials), but rejects every
// preflight (OPTIONS -> 403). So browser requests must stay "simple": POST with a text/plain body
// and no custom headers (no X-Goog-*, X-YouTube-*, Authorization, application/json).
// The JSON body is still parsed by YouTube. /api/timedtext also allows cross-origin GETs.

import { YouTubeError, isAbortError } from './errors';

export type FetchLike = (input: string, init?: RequestInit) => Promise<Response>;

export type PlayerClientName = 'ANDROID' | 'IOS' | 'ANDROID_VR';

interface ClientProfile {
  /** `context.client` fields (without hl/gl/visitorData). */
  context: Record<string, string | number>;
  /** Numeric client id for the X-YouTube-Client-Name header (server mode only). */
  nameId: string;
  /** Native user agent (server mode only; browsers send their own and cannot override it). */
  userAgent?: string;
}

// Keep these versions reasonably fresh: YouTube rejects outdated ones with
// HTTP 400 "Precondition check failed" (e.g. ANDROID 19.44.38 in Oct 2026).
// Reference values: youtubei.js src/utils/Constants.ts, yt-dlp yt_dlp/extractor/youtube/_base.py.
export const CLIENT_PROFILES: Record<PlayerClientName | 'WEB', ClientProfile> = {
  WEB: {
    context: { clientName: 'WEB', clientVersion: '2.20261006.01.00', platform: 'DESKTOP' },
    nameId: '1',
  },
  ANDROID: {
    context: {
      clientName: 'ANDROID',
      clientVersion: '21.03.36',
      androidSdkVersion: 36,
      osName: 'Android',
      osVersion: '16',
      platform: 'MOBILE',
    },
    nameId: '3',
    userAgent: 'com.google.android.youtube/21.03.36 (Linux; U; Android 16; en_US) gzip',
  },
  IOS: {
    context: {
      clientName: 'IOS',
      clientVersion: '20.11.6',
      deviceMake: 'Apple',
      deviceModel: 'iPhone10,4',
      osName: 'iOS',
      osVersion: '16.7.7.20H330',
      platform: 'MOBILE',
    },
    nameId: '5',
    userAgent: 'com.google.ios.youtube/20.11.6 (iPhone10,4; U; CPU iOS 16_7_7 like Mac OS X)',
  },
  ANDROID_VR: {
    // Needs visitorData (otherwise "Sign in to confirm you're not a bot").
    context: {
      clientName: 'ANDROID_VR',
      clientVersion: '1.65.10',
      androidSdkVersion: 32,
      deviceMake: 'Oculus',
      deviceModel: 'Quest 3',
      osName: 'Android',
      osVersion: '12L',
      platform: 'MOBILE',
    },
    nameId: '28',
    userAgent:
      'com.google.android.apps.youtube.vr.oculus/1.65.10 (Linux; U; Android 12L; eureka-user Build/SQ3A.220605.009.A1) gzip',
  },
};

export interface TransportOptions {
  fetch?: FetchLike;
  signal?: AbortSignal;
  /** Interface language for titles/dates/error texts. Default "en" (error classification relies on English texts). */
  hl?: string;
  /** Content region. Default "US". */
  gl?: string;
  /**
   * Server only: send native client headers (User-Agent, X-YouTube-Client-*). Must stay false in the
   * browser - custom headers trigger a CORS preflight, which YouTube rejects.
   */
  clientHeaders?: boolean;
  /**
   * Server only: extra request headers, e.g. a YouTube `cookie` (+ matching `authorization`
   * SAPISIDHASH) or `authorization: Bearer <oauth token>` once signed-in requests are added.
   */
  headers?: Record<string, string>;
  /** API origin. Default https://www.youtube.com (https://youtubei.googleapis.com works too). */
  apiBase?: string;
}

/**
 * Low-level access to the three calls we need. The default implementation uses fetch; the server
 * can swap in another one (e.g. youtubei.js with cookies/OAuth/PO tokens, or a proxying fetch).
 */
export interface InnertubeTransport {
  /** WEB /next: title, channel, description, date, chapters, visitorData. */
  next(videoId: string): Promise<unknown>;
  /** /player with a native client: videoDetails, playabilityStatus, caption tracks. */
  player(videoId: string, client: PlayerClientName, visitorData?: string): Promise<unknown>;
  /** GET of a caption track URL; returns the response body ('' if YouTube sent an empty body). */
  timedtext(url: string): Promise<string>;
}

export function createFetchTransport(options: TransportOptions = {}): InnertubeTransport {
  const doFetch: FetchLike = options.fetch ?? ((input, init) => globalThis.fetch(input, init));
  const hl = options.hl ?? 'en';
  const gl = options.gl ?? 'US';
  const apiBase = (options.apiBase ?? 'https://www.youtube.com').replace(/\/+$/, '');

  async function send(url: string, init: RequestInit): Promise<Response> {
    let response: Response;
    try {
      response = await doFetch(url, {
        ...init,
        signal: options.signal,
        // Never send the user's YouTube cookies from the browser; YouTube's CORS responses for
        // /youtubei do not allow credentials anyway.
        credentials: 'omit',
        referrerPolicy: 'no-referrer',
      });
    } catch (error) {
      if (isAbortError(error)) throw error;
      throw new YouTubeError('network', `Request to YouTube failed: ${String((error as Error)?.message ?? error)}`, {
        cause: error,
      });
    }
    if (response.status === 429) {
      throw new YouTubeError('rate_limited', 'YouTube rate limit reached (HTTP 429). Try again later.');
    }
    return response;
  }

  async function innertube(endpoint: 'next' | 'player', client: ClientProfile, body: Record<string, unknown>, visitorData?: string) {
    const headers: Record<string, string> = {
      // text/plain = CORS-safelisted content type -> no preflight. YouTube parses the JSON anyway.
      'content-type': 'text/plain;charset=UTF-8',
    };
    if (options.clientHeaders) {
      headers['x-youtube-client-name'] = client.nameId;
      headers['x-youtube-client-version'] = String(client.context.clientVersion);
      if (client.userAgent) headers['user-agent'] = client.userAgent;
      if (visitorData) headers['x-goog-visitor-id'] = visitorData;
    }
    Object.assign(headers, options.headers);

    const context = {
      client: { ...client.context, hl, gl, ...(visitorData ? { visitorData } : {}) },
      user: { lockedSafetyMode: false },
    };
    const response = await send(`${apiBase}/youtubei/v1/${endpoint}?prettyPrint=false`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ context, ...body }),
    });
    const text = await response.text();
    let json: unknown;
    try {
      json = JSON.parse(text);
    } catch {
      throw new YouTubeError('bad_response', `YouTube /${endpoint} returned HTTP ${response.status} with a non-JSON body.`);
    }
    if (!response.ok) {
      const message = (json as { error?: { message?: string } })?.error?.message ?? text.slice(0, 200);
      throw new YouTubeError('bad_response', `YouTube /${endpoint} (${String(client.context.clientName)}) returned HTTP ${response.status}: ${message}`);
    }
    return json;
  }

  return {
    next: (videoId) => innertube('next', CLIENT_PROFILES.WEB, { videoId }),
    player: (videoId, clientName, visitorData) =>
      innertube('player', CLIENT_PROFILES[clientName], { videoId, contentCheckOk: true, racyCheckOk: true }, visitorData),
    async timedtext(url) {
      const headers: Record<string, string> = { ...options.headers };
      const response = await send(url, { method: 'GET', headers });
      if (!response.ok) {
        throw new YouTubeError('bad_response', `YouTube timedtext returned HTTP ${response.status}.`);
      }
      return response.text();
    },
  };
}
